import React, { useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  SafeAreaView,
  StatusBar,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
} from 'react-native';
import { 
  Button, 
  Input, 
  PasswordInput, 
  OTPInput, 
  Logo, 
  DualPaneLayout,
  colors, 
  spacing, 
  typography 
} from '../components';
import { useAppContext } from '../contexts/AppContext';
import { useAppStore } from '../store/appStore';
import { apiService } from '../services/api';
import { handleApiError, logError } from '../utils/errorHandler';
import { useLocale } from '../contexts/LocaleContext';
import { useTypography } from '../hooks/useTypography';
import { useThemedStyles } from '../hooks/useThemedStyles';

export const ResetPasswordScreen: React.FC = () => {
  const styles = useThemedStyles(getStyles);
  const { 
    handleBackToLogin: onBack,
    showNotification,
    navigateTo
  } = useAppContext();
  
  const { pendingEmail, setPendingEmail } = useAppStore();
  const { t } = useLocale();
  const typo = useTypography();
  
  // Check if this is for invitation password setup
  const invitationData = (global as any).invitationData;
  const invitationToken = (global as any).invitationToken;
  const isInvitationSetup = !!invitationData && !!invitationToken;
  
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<{ 
    resetCode?: string; 
    newPassword?: string; 
    confirmPassword?: string; 
  }>({});

  const validateForm = () => {
    const newErrors: { 
      resetCode?: string; 
      newPassword?: string; 
      confirmPassword?: string; 
    } = {};

    // Skip reset code validation for invitation setup
    if (!isInvitationSetup) {
      if (!resetCode.trim()) {
        newErrors.resetCode = t('validation.required');
      } else if (resetCode.length !== 4) {
        newErrors.resetCode = t('validation.required');
      }
    }

    if (!newPassword.trim()) {
      newErrors.newPassword = t('validation.required');
    } else if (newPassword.length < 6) {
      newErrors.newPassword = t('validation.passwordMin');
    }

    if (!confirmPassword.trim()) {
      newErrors.confirmPassword = t('validation.required');
    } else if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = t('validation.passwordMatch');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleResetPassword = async () => {
    if (validateForm()) {
      setIsLoading(true);
      
      try {
        if (isInvitationSetup) {
          // Handle invitation password setup
          logError('Setting up password for invitation:', 'ResetPasswordScreen');
          
          const result = await apiService.setPasswordForInvitedUser(invitationToken, newPassword);
          
          if (result.statusCode === 1000) {
            // Clear invitation data
            (global as any).invitationData = undefined;
            (global as any).invitationToken = undefined;
            
            // Show success notification
            showNotification('success', 'Password Set', 'Your password has been set successfully! Welcome to the team!');
            
            // Navigate to login after a delay
            setTimeout(() => {
              navigateTo('unified-login');
            }, 2000);
          } else {
            showNotification('error', 'Setup Failed', result.errors?.[0]?.message || 'Failed to set password');
          }
        } else {
          // Handle normal password reset
          logError('Resetting password with code:', 'ResetPasswordScreen');
          
          await apiService.resetPassword(pendingEmail, resetCode, newPassword);
          
          // Show success notification
          showNotification('success', 'Password Reset', 'Your password has been reset successfully!');
          
          // Clear pending email
          setPendingEmail('');
          
          // Navigate back to login after a delay
          setTimeout(() => {
            navigateTo('unified-login');
          }, 2000);
        }
      } catch (error: any) {
        logError(error, 'ResetPasswordScreen');
        
        // Handle error with unified error handling
        const { title, message, type } = handleApiError(error);
        showNotification(type, title, message);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleResendResetCode = async () => {
    setIsLoading(true);
    
    try {
      logError('Resending reset code to:', 'ResetPasswordScreen');
      
      await apiService.forgotPassword(pendingEmail);
      
      // Show success notification
      showNotification('success', 'Code Resent', 'Password reset code has been resent to your email.');
    } catch (error: any) {
      logError(error, 'ResetPasswordScreen');
      
      // Handle error with unified error handling
      const { title, message, type } = handleApiError(error);
      showNotification(type, title, message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <DualPaneLayout
      title="DISLIX"
      subtitle={isInvitationSetup ? t('auth.setPassword') : t('auth.resetPassword')}
    >
      <SafeAreaView style={styles.container}>
        <StatusBar barStyle="dark-content" backgroundColor={colors.white} />
        
        <KeyboardAvoidingView 
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
          style={styles.keyboardAvoidingView}
          keyboardVerticalOffset={Platform.OS === 'ios' ? 0 : 20}
        >
          {/* Header */}
          <View style={styles.header}>
            <Logo size="medium" variant="white" />
          </View>

          {/* Main Content */}
          <ScrollView contentContainerStyle={styles.content}>
            <Text style={[styles.title, { fontFamily: typo.fontFamily.bold }]}>
              {isInvitationSetup ? t('auth.setPassword') : t('auth.resetPassword')}
            </Text>
            <Text style={[styles.subtitle, { fontFamily: typo.fontFamily.primary }]}>
              {isInvitationSetup ? t('auth.setPasswordSubtitle') : t('auth.resetPasswordSubtitle')}
            </Text>

            {/* Form */}
            <View style={styles.form}>
              {!isInvitationSetup && (
                <OTPInput
                  value={resetCode}
                  onChangeText={setResetCode}
                  length={4}
                  disabled={isLoading}
                  autoFocus={true}
                />
              )}

              <PasswordInput
                label={t('auth.newPassword')}
                value={newPassword}
                onChangeText={setNewPassword}
                placeholder={t('auth.passwordPlaceholder')}
                error={errors.newPassword}
                editable={!isLoading}
              />

              <PasswordInput
                label={t('auth.confirmPassword')}
                value={confirmPassword}
                onChangeText={setConfirmPassword}
                placeholder={t('auth.confirmPasswordPlaceholder')}
                error={errors.confirmPassword}
                editable={!isLoading}
              />

              <Button
                title={isInvitationSetup ? t('auth.setPassword') : t('auth.resetPassword')}
                onPress={handleResetPassword}
                variant="primary"
                size="lg"
                fullWidth
                loading={isLoading}
                style={styles.resetButton}
              />
            </View>
          </ScrollView>
        </KeyboardAvoidingView>
      </SafeAreaView>
    </DualPaneLayout>
  );
};

const getStyles = () => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background.surface,
  },
  keyboardAvoidingView: {
    flex: 1,
  },
  header: {
    alignItems: 'center',
    paddingHorizontal: spacing.screenPadding,
    paddingTop: spacing.sm,
    paddingBottom: spacing.md,
  },
  logoContainer: {
    alignItems: 'center',
  },
  content: {
    flex: 1,
    paddingHorizontal: spacing.screenPadding,
    paddingTop: spacing.lg,
  },
  title: {
    fontSize: typography.fontSize['2xl'],
    fontWeight: '700',
    color: colors.textDark,
    textAlign: 'center',
    marginBottom: spacing.sm,
  },
  subtitle: {
    fontSize: typography.fontSize.base,
    color: colors.textLight,
    textAlign: 'center',
    marginBottom: spacing.xl,
    lineHeight: 22,
  },
  emailText: {
    color: colors.primary,
    fontWeight: '500',
    fontFamily: typography.fontFamily.medium,
  },
  form: {
    marginBottom: spacing.xl,
  },
  resetButton: {
    marginTop: spacing.lg,
    marginBottom: spacing.xl,
  },
  resendContainer: {
    alignItems: 'center',
    marginBottom: spacing.xl,
  },
  resendText: {
    fontSize: typography.fontSize.base,
    color: colors.textLight,
    fontFamily: typography.fontFamily.primary,
  },
  resendLink: {
    color: colors.primary,
    fontWeight: '500',
    fontFamily: typography.fontFamily.medium,
  },
  tipsContainer: {
    backgroundColor: colors.primaryTransparent,
    padding: spacing.md,
    borderRadius: spacing.borderRadius.md,
    borderLeftWidth: 4,
    borderLeftColor: colors.primary,
  },
  tipsTitle: {
    fontSize: typography.fontSize.base,
    fontWeight: '500',
    color: colors.textDark,
    marginBottom: spacing.sm,
    fontFamily: typography.fontFamily.medium,
  },
  tipText: {
    fontSize: typography.fontSize.sm,
    color: colors.textLight,
    marginBottom: spacing.xs,
    fontFamily: typography.fontFamily.primary,
  },
  disabledText: {
    color: colors.textLight,
  },
  backContainer: {
    alignItems: 'center',
    marginTop: spacing.md,
  },
  backLink: {
    color: colors.primary,
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.medium,
  },
});
